import { BaseViewStore } from 'core/stores';
import ChannelStore from './ChannelStore';
import { ApplicationStore } from 'platform/application/stores';
import { WarehouseCodesForm } from 'modules/order/forms';
import { action, computed, makeObservable } from 'mobx';
import { channelType, sourceOfChange, viewStoreName } from 'common/constants';
import { BaasicDropdownViewStore } from 'common/stores';
import _ from 'lodash';

/** Why a warehouse list could not be offered. NO_CREDENTIALS is ours; the rest come back from the API. */
const listError = {
	noCredentials: 'NO_CREDENTIALS',
	unavailable: 'UNAVAILABLE',
};

class WarehouseCodesViewStore extends BaseViewStore {
	channel;
	warehouses = [];

	/** ShipStation names the two columns can be mapped to, fetched once per modal open. */
	shipFromOptions = [];
	inventoryOptions = [];
	shipStationShipFromError = null;
	shipStationInventoryError = null;

	form = new WarehouseCodesForm({
		onSuccess: async () => {
			await this.updateChannelWarehouseCodes();
		},
	});

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.channelId = modalParams.data.selectedItem ? modalParams.data.selectedItem.id : null;
		this.channelStore = new ChannelStore(rootStore);
		this.applicationStore = new ApplicationStore(rootStore);
		this.isListOrEdit =
			modalParams.data.store_path === viewStoreName.channelListViewStore || modalParams.data.isEdit;
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		if (this.modalParams.data.store_path === viewStoreName.channelListViewStore || this.modalParams.data.isEdit) {
			this.channel = await this.channelStore.getChannel(this.channelId, {
				embed: ['seller', 'convertWarehouses', 'channelType'],
			});
			this.warehouses = (await this.applicationStore.find({
				search: null,
				// Matches the create-mode fetch in ChannelEditViewStore — a lower value silently drops warehouses
				// from the modal for sellers that have many of them.
				rpp: 100,
				page: 1,
				warehouseOnly: true,
				isOrganizationActive: true,
				sellerWarehousesOnlyOrganizationId: this.channel.sellerId,
				includeOnlySellerWarehouses: true,
			})).item;
		} else {
			this.warehouses = this.modalParams.data.warehouses;
		}
		if (this.shouldRender(channelType.shipStation)) {
			await this.loadShipStationWarehouses();
		}
		await this.prepareData();
		this.resume();
	};

	/**
	 * Both columns hold ShipStation names that are matched at run time, so the names are fetched live and offered as
	 * dropdowns: typing them by hand is how mappings silently break. A list we cannot read is reported rather than
	 * degraded to a free-text box — there would be nothing to validate the typing against.
	 */
	@action.bound
	loadShipStationWarehouses = async () => {
		const credentials = this.getShipStationCredentials();
		if (!credentials.apiKey || !credentials.apiSecret) {
			this.shipStationShipFromError = listError.noCredentials;
			this.shipStationInventoryError = listError.noCredentials;
			return;
		}
		try {
			const response = await this.channelStore.getShipStationWarehouses(credentials);
			this.shipFromOptions = this.mapWarehouseOptions(response.shipFromWarehouses);
			this.inventoryOptions = this.mapWarehouseOptions(response.inventoryWarehouses);
			this.shipStationShipFromError = response.shipFromError;
			this.shipStationInventoryError = response.inventoryError;
		} catch (err) {
			this.shipStationShipFromError = listError.unavailable;
			this.shipStationInventoryError = listError.unavailable;
		}
	};

	/** Channel.Json is flattened onto the channel payload and already decrypted; while creating, it is still only on the form. */
	getShipStationCredentials = () => {
		const source = this.isListOrEdit ? this.channel : _.invoke(this.modalParams.data, 'form.values') || {};
		return {
			apiKey: source.apiKey,
			apiSecret: source.apiSecret,
			apiKeyV2: source.apiKeyV2,
		};
	};

	mapWarehouseOptions = warehouses =>
		_.map(warehouses, warehouse => ({
			value: warehouse.name,
			label: warehouse.name,
			extra: { isResolvable: warehouse.isResolvable },
		}));

	@action.bound
	prepareData = async () => {
		if (this.modalParams.data.store_path === viewStoreName.channelListViewStore || this.modalParams.data.isEdit) {
			for (const warehouse of this.warehouses) {
				const convertWarehouse = _.find(this.channel.convertWarehouses, convertWarehouse => {
					return convertWarehouse.warehouseId === warehouse.id;
				});
				await this.addWarehouseCode({
					warehouseName: warehouse.identifier,
					name: convertWarehouse ? convertWarehouse.name : null, // Warehouse Code (ConvertWarehouse.Name in the database).
					altName: convertWarehouse ? convertWarehouse.altName : null,
					identifierOnChannel: convertWarehouse ? convertWarehouse.identifierOnChannel : null,
					warehouseId: warehouse.id,
					channelId: this.channel.id,
				});
			}
		} else {
			for (const convertWarehouse of this.modalParams.data.convertWarehouses || []) {
				await this.addWarehouseCode({
					warehouseName: convertWarehouse.warehouseName,
					name: convertWarehouse.name, // Warehouse Code (ConvertWarehouse.Name in the database).
					altName: convertWarehouse.altName,
					warehouseId: convertWarehouse.warehouseId,
					identifierOnChannel: convertWarehouse.identifierOnChannel,
				});
			}
		}
	};

	/**
	 * Adds one row and, for ShipStation, hangs a dropdown store off it per mappable column. The stores write the
	 * selected NAME straight into the form field, so the save payload keeps its existing shape.
	 */
	@action.bound
	addWarehouseCode = async values => {
		this.form.$('warehouseCodes').add([values]);
		if (!this.shouldRender(channelType.shipStation)) return;

		const field = this.form.$('warehouseCodes').$(this.form.$('warehouseCodes').value.length - 1);
		if (!this.shipStationShipFromError) {
			field.shipFromDropdownStore = await this.createWarehouseDropdown(field, 'name', this.shipFromOptions);
		}
		if (!this.shipStationInventoryError) {
			field.inventoryDropdownStore = await this.createWarehouseDropdown(field, 'identifierOnChannel', this.inventoryOptions);
		}
	};

	createWarehouseDropdown = async (field, fieldName, options) => {
		const storedValue = field.$(fieldName).value;
		const dropdownStore = new BaasicDropdownViewStore();
		await dropdownStore.initialize({
			field: field.$(fieldName),
			itemTemplate: { value: 'value', label: 'label' },
			staticOptions: this.withOrphanOption(options, storedValue),
			value: storedValue ? { value: storedValue, label: storedValue } : null,
		});
		return dropdownStore;
	};

	/**
	 * A saved mapping ShipStation no longer has is kept as an option of its own, so the value stays visible (and
	 * flagged) instead of the dropdown silently rendering blank and the mapping being lost on the next save.
	 */
	withOrphanOption = (options, storedValue) => {
		if (!storedValue || this.findWarehouseOption(options, storedValue)) return options;
		return [...options, { value: storedValue, label: storedValue, extra: { isResolvable: false, isOrphan: true } }];
	};

	/** Matched the way the runtime resolvers match: trimmed and case-insensitive. */
	findWarehouseOption = (options, value) =>
		_.find(options, option => _.toLower(_.trim(option.value)) === _.toLower(_.trim(value)));

	/**
	 * Whether a saved mapping still resolves on ShipStation. "Exists" is not enough — a name the resolvers cannot tell
	 * apart is reported as broken, because inventory push and order sync skip it just the same.
	 */
	getWarehouseStatus = (item, isShipFrom) => {
		if (!this.shouldRender(channelType.shipStation)) return null;
		if (isShipFrom ? this.shipStationShipFromError : this.shipStationInventoryError) return null;

		const value = item.$(isShipFrom ? 'name' : 'identifierOnChannel').value;
		if (_.isEmpty(_.trim(value))) return null;

		const match = this.findWarehouseOption(isShipFrom ? this.shipFromOptions : this.inventoryOptions, value);
		if (!match) {
			return {
				isValid: false,
				message: isShipFrom
					? 'WAREHOUSECODES.SHIPSTATIONSHIPFROMMISSING'
					: 'WAREHOUSECODES.SHIPSTATIONINVENTORYMISSING',
			};
		}
		if (!_.get(match, 'extra.isResolvable')) {
			return {
				isValid: false,
				message: isShipFrom
					? 'WAREHOUSECODES.SHIPSTATIONSHIPFROMAMBIGUOUS'
					: 'WAREHOUSECODES.SHIPSTATIONINVENTORYAMBIGUOUS',
			};
		}
		return { isValid: true, message: 'WAREHOUSECODES.SHIPSTATIONMAPPINGOK' };
	};

	get shipStationInvalidCount() {
		if (!this.shouldRender(channelType.shipStation)) return 0;
		const rowStatuses = this.form
			.$('warehouseCodes')
			.map(item => [this.getWarehouseStatus(item, true), this.getWarehouseStatus(item, false)]);
		return _.filter(rowStatuses, statuses => _.some(statuses, status => status && !status.isValid)).length;
	}

	shouldRender = channelType => {
		const { isEdit, store_path, channelTypeDropdownStore } = this.modalParams.data;
		if (store_path === viewStoreName.channelListViewStore || isEdit) {
			return this.channel && this.channel.channelType.name === channelType;
		} else {
			return _.get(channelTypeDropdownStore, 'value.label') === channelType;
		}
	};

	@action.bound
	updateChannelWarehouseCodes = async () => {
		this.suspend();
		try {
			const { warehouseCodes } = this.form.values();
			if (this.modalParams.data.store_path === viewStoreName.channelListViewStore || this.modalParams.data.isEdit) {
				await this.channelStore.updateChannelWarehouseCodes(
					{
						channelId: this.channel.id,
						convertWarehouses: warehouseCodes,
					},
					this.modalParams.data.store_path === 'channelListViewStore'
						? sourceOfChange.channelListUpdatelWarehouseCodes
						: sourceOfChange.channelEditUpdateWarehouseCodes
				);
				this.rootStore.notificationStore.success(['WAREHOUSECODES.SUCCESS', { channelName: this.channel.name }]);
			} else {
				this.modalParams.data.convertWarehouses = warehouseCodes;
				this.rootStore.notificationStore.success(['WAREHOUSECODES.UPDATED']);
			}
			this.modalParams.success();
		} catch (err) {
			if (err.data && err.data.message) {
				this.rootStore.notificationStore.error([err.data.message]);
			} else {
				this.rootStore.notificationStore.error(['WAREHOUSECODES.ERROR', { channelName: this.channel.name }]);
			}
		}
		this.resume();
	};
}

export default WarehouseCodesViewStore;
